import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  TIPUS_ACCES,
  useCatalegPermisos,
  useDesa,
  useDesaPermisosRol,
  useElimina,
  useEtag,
  usePermisosRol,
  useRols,
  type PermisDto,
  type RolDto,
} from '@/api/administracio';
import { useCreaRol, usePersonesRol, useUsuarisPerRol } from '@/api/ajustosPersones';
import type { TipusAcces } from '@/api/types';
import { Icona } from '@/components/Icona';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Avatar, Buit } from '@/pages/operacio/comu';
import { useMe } from '@/permisos/MeContext';
import { Cerca, ErrorDesa } from './comu';
import { BarraCanvis } from './marc/canvis';
import { Calaix, Confirmacio } from './marc/Calaix';
import { useGuardaCanvis } from './marc/canvisContext';
import './administracio.css';
import './persones.css';

const clauPermis = (recurs: string, tipus: TipusAcces) => `${recurs}|${tipus}`;

/** Agrupació dels recursos per família. */
const GRUPS: Record<string, string> = {
  calendaris: 'guardies',
  torns: 'guardies',
  sollicituds: 'guardies',
  guardies: 'guardies',
  experts: 'guardies',
  activacions: 'activacions',
  organitzacio: 'organitzacio',
  'usuaris-informar': 'organitzacio',
  documents: 'organitzacio',
  circuits: 'organitzacio',
  notificacions: 'notificacions',
  plantilles: 'notificacions',
  usuaris: 'administracio',
  rols: 'administracio',
  configuracio: 'administracio',
  integracions: 'administracio',
  'app-versions': 'administracio',
  informes: 'informes',
  auditoria: 'informes',
  monitoratge: 'informes',
};
const ORDRE_GRUPS = [
  'guardies',
  'activacions',
  'organitzacio',
  'notificacions',
  'administracio',
  'informes',
  'altres',
];
const grupDe = (recurs: string) => GRUPS[recurs.split('.')[1] ?? ''] ?? 'altres';

interface FilaRecurs {
  recurs: string;
  descripcio: string;
  permisos: Partial<Record<TipusAcces, PermisDto>>;
}

const nomRolDe = (r: RolDto, es: boolean) => (es ? r.nomEs : r.nomCa);

/** Casella de grup: marcada si tot el grup ho està, indeterminada si només una part. */
function CasellaGrup({
  marcats,
  total,
  etiqueta,
  disabled,
  onCanvia,
}: {
  marcats: number;
  total: number;
  etiqueta: string;
  disabled: boolean;
  onCanvia: (marca: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = marcats > 0 && marcats < total;
  }, [marcats, total]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={etiqueta}
      checked={total > 0 && marcats === total}
      disabled={disabled}
      onChange={() => onCanvia(marcats < total)}
    />
  );
}

function Matriu({ rol }: { rol: RolDto }) {
  const { t, i18n } = useTranslation('administracio');
  const { pot } = useMe();
  const cataleg = useCatalegPermisos();
  const actuals = usePermisosRol(rol.id ?? null);
  const desa = useDesaPermisosRol();
  const [edicio, setEdicio] = useState<Set<string> | null>(null);
  const [text, setText] = useState('');
  const es = i18n.language === 'es';
  // RF-003: fins que no s'han carregat els permisos actuals, la matriu no és editable (un clic
  // prematur partiria d'un conjunt buit i en desar s'esborrarien tots).
  const carregat = actuals.isSuccess && cataleg.isSuccess;
  const inicials = useMemo(
    () => new Set((actuals.data ?? []).map((p) => clauPermis(p.recurs, p.tipusAcces))),
    [actuals.data],
  );
  const marcats = edicio ?? inicials;
  const editable = pot('cai.rols', 'MODIFICAR') && !rol.sistema;

  const grups = useMemo(() => {
    const filtre = text.trim().toLowerCase();
    const files = new Map<string, FilaRecurs>();
    for (const p of cataleg.data ?? []) {
      const f = files.get(p.recurs) ?? { recurs: p.recurs, descripcio: '', permisos: {} };
      f.permisos[p.tipusAcces] = p;
      const d = (es ? p.descripcioEs : p.descripcioCa) ?? '';
      if (d && (!f.descripcio || p.tipusAcces === 'CONSULTAR')) f.descripcio = d;
      files.set(p.recurs, f);
    }
    const llista = [...files.values()]
      .filter(
        (f) =>
          !filtre ||
          f.recurs.toLowerCase().includes(filtre) ||
          f.descripcio.toLowerCase().includes(filtre),
      )
      .sort((a, b) => a.recurs.localeCompare(b.recurs));
    return ORDRE_GRUPS.map(
      (g) => [g, llista.filter((f) => grupDe(f.recurs) === g)] as const,
    ).filter(([, f]) => f.length > 0);
  }, [cataleg.data, text, es]);

  const canvis =
    [...marcats].filter((k) => !inicials.has(k)).length +
    [...inicials].filter((k) => !marcats.has(k)).length;

  const posa = (claus: string[], marca: boolean) => {
    if (!carregat || !editable) return;
    const n = new Set(marcats);
    for (const k of claus) {
      if (marca) n.add(k);
      else n.delete(k);
    }
    setEdicio(n);
  };

  const guarda = () => {
    if (!carregat || rol.id == null) return;
    const permisos = (cataleg.data ?? []).filter((p) =>
      marcats.has(clauPermis(p.recurs, p.tipusAcces)),
    );
    desa.mutate({ id: rol.id, permisos }, { onSuccess: () => setEdicio(null) });
  };

  return (
    <div className="stack">
      {rol.sistema ? (
        <div className="info-banda">
          <Icona nom="lock" />
          {t('persones.rols.sistemaAvis')}
        </div>
      ) : null}
      <div className="panel" data-rol={rol.id}>
        <div className="panel__head">
          <h3>{t('persones.rols.queGestiona')}</h3>
          <div className="spacer" />
          <span className="t-caption-2 dim nowrap">
            {carregat ? t('rols.actius', { count: marcats.size }) : t('comu.carregant')}
          </span>
          <Cerca
            className="admin-cerca-toolbar"
            valor={text}
            onCanvia={setText}
            placeholder={t('rols.cercaRecurs')}
          />
        </div>
        {cataleg.isError || actuals.isError ? (
          <ErrorCarrega
            onReintenta={() => {
              void cataleg.refetch();
              void actuals.refetch();
            }}
          />
        ) : (
          <div className="taula-scroll">
            <table className="taula matriu-rols" aria-busy={!carregat}>
              <thead>
                <tr>
                  <th>{t('persones.rols.queGestiona')}</th>
                  {TIPUS_ACCES.map((ta) => (
                    <th key={ta} className="matriu-rols__c">
                      {t(`persones.rols.cols.${ta}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grups.map(([grup, files]) => {
                  const claus = files.flatMap((f) =>
                    TIPUS_ACCES.filter((ta) => f.permisos[ta]).map((ta) =>
                      clauPermis(f.recurs, ta),
                    ),
                  );
                  const nomGrup = t(`rols.grups.${grup}`);
                  return (
                    <Fragment key={grup}>
                      <tr className="matriu-rols__grup">
                        <td colSpan={5}>
                          <label className="hstack gap-6">
                            <CasellaGrup
                              marcats={claus.filter((k) => marcats.has(k)).length}
                              total={claus.length}
                              etiqueta={t('persones.rols.marcaGrup', { grup: nomGrup })}
                              disabled={!editable || !carregat}
                              onCanvia={(m) => posa(claus, m)}
                            />
                            {nomGrup}
                            <span className="matriu-rols__n">
                              {t('persones.rols.grupN', {
                                n: claus.filter((k) => marcats.has(k)).length,
                                total: claus.length,
                              })}
                            </span>
                          </label>
                        </td>
                      </tr>
                      {files.map((f) => (
                        <tr key={f.recurs} data-recurs={f.recurs}>
                          <td>
                            <div>{f.descripcio || f.recurs}</div>
                            <div className="codi">{f.recurs}</div>
                          </td>
                          {TIPUS_ACCES.map((ta) => {
                            const k = clauPermis(f.recurs, ta);
                            return (
                              <td key={ta} className="matriu-rols__c">
                                {f.permisos[ta] ? (
                                  <input
                                    type="checkbox"
                                    aria-label={t('rols.permisDe', {
                                      recurs: f.recurs,
                                      acces: t(`rols.acces.${ta}`),
                                    })}
                                    checked={marcats.has(k)}
                                    disabled={!editable || !carregat}
                                    onChange={(e) => posa([k], e.target.checked)}
                                  />
                                ) : (
                                  <span
                                    className="matriu-rols__na"
                                    title={t('persones.rols.noAplica')}
                                  >
                                    —
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {carregat && grups.length === 0 ? (
          <Buit icona="magnifier">{t('rols.senseResultats')}</Buit>
        ) : null}
      </div>
      {desa.isSuccess && canvis === 0 ? (
        <p className="ok-text" role="status">
          {t('persones.rols.permisosDesats')}
        </p>
      ) : null}
      <ErrorDesa error={desa.error} />
      <BarraCanvis
        n={canvis}
        nom={nomRolDe(rol, es)}
        onDesa={guarda}
        onDescarta={() => setEdicio(null)}
        desant={desa.isPending}
        textDesa={t('rols.desaPermisos')}
      />
    </div>
  );
}

function PersonesRol({ rolId }: { rolId: number }) {
  const { t } = useTranslation('administracio');
  const [page, setPage] = useState(0);
  const persones = usePersonesRol(rolId, page);
  const p = persones.data;
  if (persones.isError) return <ErrorCarrega onReintenta={() => void persones.refetch()} />;
  if (!p || p.items.length === 0)
    return (
      <Buit icona="person-3">
        {persones.isLoading ? t('comu.carregant') : t('persones.rols.sensePersones')}
      </Buit>
    );
  return (
    <div className="panel">
      <table className="taula">
        <thead>
          <tr>
            <th>{t('usuaris.persona')}</th>
            <th>{t('usuaris.correu')}</th>
            <th>{t('usuaris.estat')}</th>
          </tr>
        </thead>
        <tbody>
          {p.items.map((u) => {
            const nom = `${u.nom} ${u.cognoms ?? ''}`.trim();
            return (
              <tr key={u.id}>
                <td>
                  <Link
                    to={`/admin/usuaris/${u.id}`}
                    className="hstack"
                    title={t('persones.rols.veure')}
                  >
                    <Avatar nom={nom} />
                    <span>{nom}</span>
                  </Link>
                </td>
                <td className="dim">{u.email ?? u.upn ?? '—'}</td>
                <td>
                  <span className={`badge badge--${u.actiu ? 'ok' : 'gray'}`}>
                    {u.actiu ? t('comu.actiu') : t('comu.inactiu')}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {p.total > p.size ? (
        <div className="panel__foot">
          <span className="t-caption dim">{t('persones.rols.persones', { count: p.total })}</span>
          <div className="spacer" />
          <button
            type="button"
            className="btn btn--xs"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            {t('comu.anterior')}
          </button>
          <button
            type="button"
            className="btn btn--xs"
            disabled={(page + 1) * p.size >= p.total}
            onClick={() => setPage(page + 1)}
          >
            {t('comu.seguent')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function DadesRol({ rol }: { rol: RolDto }) {
  const { t } = useTranslation('administracio');
  const { pot } = useMe();
  const { etag } = useEtag('/rols', rol.id);
  const desa = useDesa<RolDto>('/rols', ['rols'], etag);
  const [r, setR] = useState<RolDto>(rol);
  const editable = pot('cai.rols', 'MODIFICAR') && !rol.sistema;
  const camps = (['nomCa', 'nomEs', 'descripcio'] as const).filter(
    (c) => (r[c] ?? '') !== (rol[c] ?? ''),
  ).length;
  const valid = r.nomCa.trim() && r.nomEs.trim();
  return (
    <div className="stack">
      <div className="panel">
        <div className="panel__body form">
          <div className="camp">
            <label className="camp__label" htmlFor="rol-codi">
              {t('rols.codi')}
            </label>
            <input id="rol-codi" className="input mono" value={rol.codi} disabled />
          </div>
          <div className="grid grid-2" style={{ gap: 12 }}>
            <div className="camp">
              <label className="camp__label" htmlFor="rol-nomCa">
                {t('rols.nomCa')}
              </label>
              <input
                id="rol-nomCa"
                className="input"
                value={r.nomCa}
                disabled={!editable}
                onChange={(e) => setR({ ...r, nomCa: e.target.value })}
              />
            </div>
            <div className="camp">
              <label className="camp__label" htmlFor="rol-nomEs">
                {t('rols.nomEs')}
              </label>
              <input
                id="rol-nomEs"
                className="input"
                value={r.nomEs}
                disabled={!editable}
                onChange={(e) => setR({ ...r, nomEs: e.target.value })}
              />
            </div>
          </div>
          <div className="camp">
            <label className="camp__label" htmlFor="rol-descripcio">
              {t('rols.descripcio')}
            </label>
            <textarea
              id="rol-descripcio"
              className="input"
              value={r.descripcio ?? ''}
              disabled={!editable}
              onChange={(e) => setR({ ...r, descripcio: e.target.value })}
            />
          </div>
        </div>
      </div>
      {desa.isSuccess && camps === 0 ? (
        <p className="ok-text" role="status">
          {t('persones.rols.dadesDesades')}
        </p>
      ) : null}
      <ErrorDesa error={desa.error} />
      <BarraCanvis
        n={camps}
        nom={rol.nomCa}
        onDescarta={() => setR(rol)}
        onDesa={() => desa.mutate(r)}
        desant={desa.isPending}
        desactivat={!valid}
      />
    </div>
  );
}

/** Calaix per crear un rol nou o duplicar-ne un (amb els seus permisos). */
function NouRol({
  origen,
  onTanca,
  onCreat,
}: {
  origen?: RolDto;
  onTanca: () => void;
  onCreat: (id: number) => void;
}) {
  const { t } = useTranslation('administracio');
  const permisos = usePermisosRol(origen?.id ?? null);
  const crea = useCreaRol();
  const [rol, setRol] = useState<RolDto>({
    codi: origen ? `${origen.codi}_COPIA` : '',
    nomCa: origen ? t('persones.rols.copiaDe', { nom: origen.nomCa, lng: 'ca' }) : '',
    nomEs: origen ? t('persones.rols.copiaDe', { nom: origen.nomEs, lng: 'es' }) : '',
    descripcio: origen?.descripcio ?? '',
    sistema: false,
  });
  const valid = rol.codi.trim() && rol.nomCa.trim() && rol.nomEs.trim();
  const llest = !origen || permisos.isSuccess;
  return (
    <Calaix
      titol={
        origen ? t('persones.rols.duplicaTitol', { rol: origen.nomCa }) : t('persones.rols.nou')
      }
      etiqueta={t('titols.rols')}
      onTanca={onTanca}
      peu={
        <>
          <button type="button" className="btn btn--s" onClick={onTanca}>
            {t('marc.confirma.cancella')}
          </button>
          <button
            type="button"
            className="btn btn--dark btn--s"
            disabled={!valid || !llest || crea.isPending}
            onClick={() =>
              crea.mutate(
                { rol, permisos: origen ? (permisos.data ?? []) : undefined },
                { onSuccess: (nou) => nou?.id != null && onCreat(nou.id) },
              )
            }
          >
            <Icona nom="checkmark" />
            {t('persones.rols.creaRol')}
          </button>
        </>
      }
    >
      <p className="t-footnote dim" style={{ margin: 0 }}>
        {origen ? t('persones.rols.duplicaAjuda') : t('persones.rols.nouAjuda')}
      </p>
      <div className="camp">
        <label className="camp__label" htmlFor="nou-rol-codi">
          {t('rols.codi')}
        </label>
        <input
          id="nou-rol-codi"
          className="input mono"
          value={rol.codi}
          onChange={(e) =>
            setRol({ ...rol, codi: e.target.value.toUpperCase().replace(/\s+/g, '_') })
          }
        />
        <span className="camp__ajuda">{t('persones.rols.codiAjuda')}</span>
      </div>
      <div className="camp">
        <label className="camp__label" htmlFor="nou-rol-ca">
          {t('rols.nomCa')}
        </label>
        <input
          id="nou-rol-ca"
          className="input"
          value={rol.nomCa}
          onChange={(e) => setRol({ ...rol, nomCa: e.target.value })}
        />
      </div>
      <div className="camp">
        <label className="camp__label" htmlFor="nou-rol-es">
          {t('rols.nomEs')}
        </label>
        <input
          id="nou-rol-es"
          className="input"
          value={rol.nomEs}
          onChange={(e) => setRol({ ...rol, nomEs: e.target.value })}
        />
      </div>
      <div className="camp">
        <label className="camp__label" htmlFor="nou-rol-desc">
          {t('rols.descripcio')}
        </label>
        <textarea
          id="nou-rol-desc"
          className="input"
          value={rol.descripcio ?? ''}
          onChange={(e) => setRol({ ...rol, descripcio: e.target.value })}
        />
      </div>
      <ErrorDesa error={crea.error} />
    </Calaix>
  );
}

type Pestanya = 'permisos' | 'persones' | 'dades';
const PESTANYES_ROL: Pestanya[] = ['permisos', 'persones', 'dades'];

export function W22Rols() {
  const { t, i18n } = useTranslation('administracio');
  const { pot } = useMe();
  const es = i18n.language === 'es';
  const rols = useRols();
  const [params, setParams] = useSearchParams();
  const guarda = useGuardaCanvis();
  const [pestanya, setPestanya] = useState<Pestanya>('permisos');
  const [calaix, setCalaix] = useState<'nou' | 'duplica' | null>(null);
  const [elimina, setElimina] = useState(false);
  const [cerca, setCerca] = useState('');
  const esborra = useElimina('/rols', ['rols']);
  const llista = rols.data ?? [];
  const perRol = useUsuarisPerRol(llista);
  const seleccionat = Number(params.get('rol')) || null;
  const actiu = llista.find((r) => r.id === seleccionat) ?? llista[0];
  const q = cerca.trim().toLowerCase();
  const visibles = llista.filter(
    (r) => !q || `${r.nomCa} ${r.nomEs} ${r.codi}`.toLowerCase().includes(q),
  );

  const tria = (id: number | null | undefined) =>
    guarda(() => {
      setParams(id != null ? { rol: String(id) } : {}, { replace: true });
      setPestanya('permisos');
    });

  return (
    <section className="pantalla" data-screen="W-22">
      <Toolbar titol={t('titols.rols')}>
        {pot('cai.rols', 'ALTA') ? (
          <>
            <button
              type="button"
              className="btn btn--outline btn--s"
              disabled={!actiu}
              onClick={() => setCalaix('duplica')}
            >
              <Icona nom="rectangle-stack" />
              {t('persones.rols.duplica')}
            </button>
            <button type="button" className="btn btn--dark btn--s" onClick={() => setCalaix('nou')}>
              <Icona nom="plus" />
              {t('persones.rols.nou')}
            </button>
          </>
        ) : null}
      </Toolbar>
      <div className="lf">
        <div className="lf__llista">
          <div className="lf__eines">
            <Cerca valor={cerca} onCanvia={setCerca} placeholder={t('rols.llista')} />
          </div>
          {rols.isError ? (
            <ErrorCarrega onReintenta={() => void rols.refetch()} />
          ) : (
            <ul className="lf__items" role="listbox" aria-label={t('rols.llista')}>
              {visibles.map((r) => {
                const n = r.id != null ? perRol.total.get(r.id) : undefined;
                return (
                  <li
                    key={r.id}
                    role="option"
                    className="lf__item"
                    aria-selected={r.id === actiu?.id}
                    tabIndex={0}
                    title={r.codi}
                    onClick={() => r.id !== actiu?.id && tria(r.id)}
                    onKeyDown={(e) => e.key === 'Enter' && r.id !== actiu?.id && tria(r.id)}
                  >
                    <span className="lf__text">
                      <span className="lf__nom">{nomRolDe(r, es)}</span>
                      {n != null ? (
                        <span className="lf__sub">{t('persones.rols.persones', { count: n })}</span>
                      ) : null}
                    </span>
                    {r.sistema ? <Icona nom="lock" className="lf__compte" /> : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="lf__fitxa">
          {actiu ? (
            <>
              <div className="fitxa-cap">
                <div className="fitxa-cap__titols">
                  <span className="fitxa-cap__ruta mono">{actiu.codi}</span>
                  <h2>{nomRolDe(actiu, es)}</h2>
                  {actiu.descripcio ? (
                    <span className="t-footnote dim">{actiu.descripcio}</span>
                  ) : null}
                  {actiu.sistema ? (
                    <div className="fitxa-cap__badges">
                      <span className="badge badge--gray">
                        <Icona nom="lock" />
                        {t('rols.sistema')}
                      </span>
                    </div>
                  ) : null}
                </div>
                {!actiu.sistema && pot('cai.rols', 'ELIMINAR') ? (
                  <div className="fitxa-cap__accions">
                    <button
                      type="button"
                      className="btn btn--danger btn--s"
                      onClick={() => setElimina(true)}
                    >
                      <Icona nom="trash" />
                      {t('persones.rols.elimina')}
                    </button>
                  </div>
                ) : null}
              </div>
              <div className="fitxa-pestanyes" role="tablist">
                {PESTANYES_ROL.map((p) => {
                  const n =
                    p === 'persones' && actiu.id != null ? perRol.total.get(actiu.id) : undefined;
                  return (
                    <button
                      key={p}
                      type="button"
                      role="tab"
                      aria-selected={pestanya === p}
                      onClick={() => p !== pestanya && guarda(() => setPestanya(p))}
                    >
                      {t(`persones.rols.pestanyes.${p}`)}
                      {n != null ? <span className="fitxa-pestanyes__n">{n}</span> : null}
                    </button>
                  );
                })}
              </div>
              <div className="fitxa-cos" role="tabpanel">
                {pestanya === 'permisos' ? <Matriu key={actiu.id} rol={actiu} /> : null}
                {pestanya === 'persones' && actiu.id != null ? (
                  <PersonesRol key={actiu.id} rolId={actiu.id} />
                ) : null}
                {pestanya === 'dades' ? (
                  <DadesRol key={`${actiu.id}-${actiu.version ?? ''}`} rol={actiu} />
                ) : null}
              </div>
            </>
          ) : (
            <Buit icona="person-key">{t('comu.carregant')}</Buit>
          )}
        </div>
      </div>
      {calaix ? (
        <NouRol
          origen={calaix === 'duplica' ? actiu : undefined}
          onTanca={() => setCalaix(null)}
          onCreat={(id) => {
            setCalaix(null);
            setParams({ rol: String(id) }, { replace: true });
            setPestanya('permisos');
          }}
        />
      ) : null}
      {elimina && actiu?.id != null ? (
        <Confirmacio
          titol={t('persones.rols.eliminaTitol', { rol: nomRolDe(actiu, es) })}
          textAccio={t('persones.rols.elimina')}
          perill
          ocupat={esborra.isPending}
          onTanca={() => setElimina(false)}
          onConfirma={() =>
            esborra.mutate(actiu.id as number, {
              onSuccess: () => {
                setElimina(false);
                setParams({}, { replace: true });
              },
            })
          }
        >
          <p>{t('persones.rols.eliminaText')}</p>
          <ErrorDesa error={esborra.error} />
        </Confirmacio>
      ) : null}
    </section>
  );
}
